<script>

  // TO FIX:
  // Map seems to crop from the left-hand side on mobile
  // Maybe drop shadow on minimap?
  // Go through charts CSS and update to use Guardian Source variables
  // Add a loading state to the map

  // Core imports
  import { onMount, tick } from "svelte"
  import { createEventDispatcher } from "svelte"
  import { getJson } from "$lib/helpers/guardian/toolbelt.js"
  import { getTimezoneInfo } from "$lib/helpers/getTimezoneInfo.js"
  // Guardian approved colour palettes
  import {
    categoricalLight,
    categoricalDark,
  } from "$lib/helpers/guardian/colours"
  import maplibregl from "maplibre-gl"
  import "maplibre-gl/dist/maplibre-gl.css"
  const { Map, ScaleControl, Popup } = maplibregl
  import { geoMercator, geoPath, geoBounds } from "d3-geo"
  import { select } from "d3-selection"

  import basemap from "$lib/mapstyles/basemap-styles.json"
  import aus from "$lib/mapstyles/aus-simple.json"

  // [[west, south], [east, north]] for Map#fitBounds, or null
  function lngLatBoundsFromGeoJSON(data) {
    if (!data?.features?.length) return null
    try {
      const b = geoBounds(data)
      if (!b || !Number.isFinite(b[0]?.[0])) return null
      let [[west, south], [east, north]] = b
      const lonSpan = east - west
      const latSpan = north - south
      if (lonSpan < 1e-8 && latSpan < 1e-8) {
        const pad = 0.4
        return [
          [west - pad, south - pad],
          [east + pad, north + pad],
        ]
      }
      return [
        [west, south],
        [east, north],
      ]
    } catch {
      return null
    }
  }

  // Add darkmode detection here at some point

  // Example for getting chart data from a store
  //import {getExampleData, exampledata} from '$lib/stores/example.svelte.js';

  let width = $state(620)
  let height = 500

  // Toggle to disable map interactions (set to false to disable zoom/pan)

  // For debugging

  // const centerPoint = {
  //       'type': 'FeatureCollection',
  //       'features': [
  //           {
  //               'type': 'Feature',
  //               'geometry': {
  //                   'type': 'Point',
  //                   'coordinates': center
  //               }
  //           }
  //       ]
  //   };
  // const centerStyle = {
  //   "id":"center-point",
  //   "type":"circle",
  //   "source":"center-point",
  //   "paint":{
  //     "circle-radius":10,
  //     "circle-color":"#000"
  //   }
  // }

  // GeoJSON styles are now provided by parent components (e.g., Atom.svelte)

  // Component props
  let {
    geojson = null,
    geoJsonStyles = [],
    currentPosition = null,
    showTimeLegend = false,
    MAP_INTERACTIVE = true,
    SHOW_NAVIGATION_CONTROL = false,
    SHOW_SCALE_CONTROL_BOTTOM_LEFT = false,
    SHOW_FULLSCREEN_CONTROL = false,
    SHOW_GEOLOCATE_CONTROL = false,
    // When true (and overlays are used), fit the map to GeoJSON bounds instead of center/zoom
    fitBoundsToGeoJson = true,
    center = [116.03196265904751, -31.90047341428921],
    zoom = 8,
    headline = "Perth bushfires",
    subtitle = "Showing bushfire warning areas. Data last checked {timestamp} (AEST). This map should not be relied on in an emergency, please check the <a href='https://www.emergency.wa.gov.au/' target='_blank'>EmergencyWA website</a> for the latest information",
    source = "Guardian graphic. Source: <a href='https://www.emergency.wa.gov.au/' target='_blank'>EmergencyWA website</a>, OpenStreetMap",
  } = $props()

  function applyViewFromGeoJson() {
    if (!mapInstance) return
    if (!fitBoundsToGeoJson || geoJsonStyles.length === 0) return
    const b = lngLatBoundsFromGeoJSON(geojson)
    if (b) {
      mapInstance.fitBounds(b, { padding: 48, maxZoom: 12, duration: 0 })
    } else {
      mapInstance.setCenter(center)
      mapInstance.setZoom(zoom)
    }
  }

  let mapInstance
  let currentMarker = null
  let forecastPopup = null
  let minimapSvg
  let minimapPath
  let viewportRect
  let minimapProjection
  let timestamp = $state(null)
  let isLoading = $state(true)
  let mapLoaded = $state(false)
  const dispatch = createEventDispatcher()

  // Function to update minimap viewport rectangle
  function updateMinimap() {
    if (!mapInstance || !minimapPath || !viewportRect) return

    try {
      // Get current map bounds from the main MapLibre map
      const bounds = mapInstance.getBounds()
      const sw = bounds.getSouthWest() // returns {lng, lat}
      const ne = bounds.getNorthEast() // returns {lng, lat}

      // Validate bounds are reasonable (within Australia's approximate bounds)
      if (sw.lng < 100 || ne.lng > 160 || sw.lat < -50 || ne.lat > 0) {
        viewportRect.attr("d", null) // Hide if bounds are invalid
        return
      }

      // Create GeoJSON polygon for viewport bounds
      // D3 expects clockwise exterior rings (area to the right of boundary)
      // Order: SW -> NW -> NE -> SE -> SW (clockwise)
      const viewportGeo = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [sw.lng, sw.lat], // SW
              [sw.lng, ne.lat], // NW
              [ne.lng, ne.lat], // NE
              [ne.lng, sw.lat], // SE
              [sw.lng, sw.lat], // close back to SW
            ],
          ],
        },
      }

      // Use minimapPath to generate the path
      const pathData = minimapPath(viewportGeo)
      if (pathData) {
        // Calculate viewport size to adjust stroke width for small rectangles
        const lonRange = ne.lng - sw.lng
        const latRange = ne.lat - sw.lat
        const area = lonRange * latRange

        // Increase stroke width when viewport is small (threshold is arbitrary, adjust as needed)
        const strokeWidth = area < 0.5 ? 4 : area < 2 ? 3 : 2

        viewportRect
          .attr("d", pathData)
          .attr("stroke-width", strokeWidth)
          .style("display", "block")
      } else {
        viewportRect.style("display", "none")
      }
    } catch (e) {
      // Silently handle errors
    }
  }

  // Reactively update GeoJSON source when data arrives after map loads
  $effect(() => {
    if (!mapLoaded || !geojson || !mapInstance) return
    console.time("[carto] setData")
    const source = mapInstance.getSource("overlays")
    if (source) {
      source.setData(geojson)
      if (fitBoundsToGeoJson) applyViewFromGeoJson()
      console.timeEnd("[carto] setData")
      console.log(`[carto] overlays source updated — ${geojson.features?.length} features`)
    } else {
      console.timeEnd("[carto] setData")
      console.warn("[carto] overlays source not found on map")
    }
  })

  // Watch for width changes and resize map
  $effect(() => {
    // Track width changes
    width
    if (mapInstance && mapInstance.loaded()) {
      // Wait for DOM to update, then resize the map
      tick().then(() => {
        // Use double requestAnimationFrame to ensure browser has painted
        // and container dimensions are updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            mapInstance.resize()
          })
        })
      })
    }
  })

  // Component lifecycle
  onMount(async () => {
    console.time("[carto] onMount → map load")
    console.log("[carto] onMount — geojson at init:", geojson ? `${geojson.features?.length} features` : "null")

    // Conditionally fetch GeoJSON if a URL is provided
    let overlaysSource = null
    let combinedLayers = [...basemap]

    if (geoJsonStyles.length > 0) {
      overlaysSource = {
        type: "geojson",
        data: geojson || { type: "FeatureCollection", features: [] },
      }
      combinedLayers = [...basemap, ...geoJsonStyles]
    }

    let mapDefs = {
      version: 8,
      sources: (() => {
        const sources = {
          "vector-tiles": {
            type: "vector",
            tiles: [
              "https://interactive.guim.co.uk/maptiles/world/{z}/{x}/{y}.pbf",
            ],
          },
        }
        if (overlaysSource) {
          sources["overlays"] = overlaysSource
        }
        return sources
      })(),
        // 'center-point': {
        //   "type": "geojson",
        //   "data": centerPoint
        // }
      // Omit sprite: basemap place labels are text-only (no dot_9 / external sprite).
      glyphs:
        "https://interactive.guim.co.uk/maptiles/fonts/{fontstack}/{range}.pbf",
      layers: combinedLayers,
    }

    console.time("[carto] map constructor")
    mapInstance = new Map({
      container: "cartoMap",
      style: mapDefs,
      center: center,
      zoom: zoom,
      attributionControl: false,
      maplibreLogo: false,
      cooperativeGestures: width < 480 ? true : false,
    })
    console.timeEnd("[carto] map constructor")

    // Add diagonal hatch for UNK hazard fills before first paint (see styleimagemissing fallback below)
    function addDiagonalPatternImage() {
      const size = 32
      const canvas = document.createElement("canvas")
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext("2d")
      ctx.strokeStyle = "#888"
      ctx.lineWidth = 1.5
      for (let i = -size; i < size * 2; i += 4) {
        ctx.beginPath()
        ctx.moveTo(i, -size)
        ctx.lineTo(i + size, size * 2)
        ctx.stroke()
      }
      const imgData = ctx.getImageData(0, 0, size, size)
      if (!mapInstance.hasImage("diagonal-lines-grey")) {
        mapInstance.addImage("diagonal-lines-grey", {
          width: size,
          height: size,
          data: new Uint8Array(imgData.data),
        })
      }
    }

    // Pre-render category number labels as canvas images (avoids glyph/font loading)
    function addCategoryLabelImages() {
      const pr = window.devicePixelRatio || 1
      const size = Math.round(20 * pr)
      const fontSize = Math.round(11 * pr)
      const configs = [
        { cat: "1", textColor: "#333", halo: "rgba(255,255,255,0.9)" },
        { cat: "2", textColor: "#333", halo: "rgba(255,255,255,0.9)" },
        { cat: "3", textColor: "#fff", halo: "rgba(0,0,0,0.6)" },
        { cat: "4", textColor: "#fff", halo: "rgba(0,0,0,0.6)" },
        { cat: "5", textColor: "#fff", halo: "rgba(0,0,0,0.6)" },
      ]
      for (const { cat, textColor, halo } of configs) {
        if (mapInstance.hasImage(`cat-label-${cat}`)) continue
        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext("2d")
        ctx.font = `bold ${fontSize}px sans-serif`
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        // Halo pass
        ctx.fillStyle = halo
        const r = Math.max(1, Math.round(pr))
        for (const [dx, dy] of [[-r,0],[r,0],[0,-r],[0,r],[-r,-r],[r,-r],[-r,r],[r,r]]) {
          ctx.fillText(cat, size / 2 + dx, size / 2 + dy)
        }
        // Text pass
        ctx.fillStyle = textColor
        ctx.fillText(cat, size / 2, size / 2)
        const imgData = ctx.getImageData(0, 0, size, size)
        mapInstance.addImage(`cat-label-${cat}`, { width: size, height: size, data: new Uint8Array(imgData.data) }, { pixelRatio: pr })
      }
    }

    mapInstance.on("styleimagemissing", (e) => {
      if (e.id === "diagonal-lines-grey") {
        addDiagonalPatternImage()
      }
      if (e.id.startsWith("cat-label-")) {
        addCategoryLabelImages()
      }
    })

    // Disable interactions if MAP_INTERACTIVE is false
    if (!MAP_INTERACTIVE) {
      mapInstance.dragPan.disable()
      mapInstance.scrollZoom.disable()
      mapInstance.boxZoom.disable()
      mapInstance.doubleClickZoom.disable()
      mapInstance.touchZoomRotate.disable()
    }

    if (SHOW_NAVIGATION_CONTROL) {
      mapInstance.addControl(new maplibregl.NavigationControl(), "top-right")
    }
    if (SHOW_SCALE_CONTROL_BOTTOM_LEFT) {
      mapInstance.addControl(
        new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }),
        "bottom-left",
      )
    }
    if (SHOW_FULLSCREEN_CONTROL) {
      mapInstance.addControl(new maplibregl.FullscreenControl(), "top-right")
    }
    if (SHOW_GEOLOCATE_CONTROL) {
      mapInstance.addControl(
        new maplibregl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
        }),
        "top-right",
      )
    }



    mapInstance.addControl(
      new ScaleControl({
        unit: "metric",
      }),
      "bottom-right",
    )

    // Set up minimap
    const minimapWidth = 150
    const minimapHeight = 100

    // Use identity projection for Web Mercator coordinates
    minimapProjection = geoMercator().fitExtent(
      [
        [0, 0],
        [minimapWidth, minimapHeight],
      ],
      aus,
    )

    minimapPath = geoPath().projection(minimapProjection)

    // Create SVG for minimap
    minimapSvg = select("#minimap")
      .append("svg")
      .attr("width", minimapWidth)
      .attr("height", minimapHeight)

    minimapSvg
      .append("g")
      .selectAll("path")
      .data(aus.features)
      .enter()
      .append("path")
      .attr("d", minimapPath)
      .attr("fill", "#FFF")
      .attr("stroke", "#000")
      .attr("stroke-width", 1)
      .attr("fill-rule", "evenodd")

    // Add viewport rectangle (on top of Australia)
    viewportRect = minimapSvg
      .append("path")
      .attr("fill", "rgba(255, 0, 0, 0.6)")
      .attr("stroke", "#c70000")
      .attr("stroke-width", 2)

    // Function to log map center and zoom
    function logMapState() {
      const center = mapInstance.getCenter()
      const zoom = mapInstance.getZoom()
      //console.log(`${center.lng}, ${center.lat}, 'Zoom:', ${zoom}`)
    }

    // Function to set up map after it loads
    function setupMapAfterLoad() {
      try {
        console.timeEnd("[carto] onMount → map load")
        console.log("[carto] map load event fired")
        addDiagonalPatternImage()
        // Add pulse marker for current cyclone position
        if (currentMarker) {
          currentMarker.remove()
          currentMarker = null
        }
        if (currentPosition) {
          
          const bearing = currentPosition.bearing ?? 0
          const el = document.createElement("div")
          el.className = "cyclone-pulse-marker"
          el.innerHTML = `<span class="pulse-ring"></span><span class="pulse-arrow" style="transform: translate(-50%, -50%) rotate(${bearing}deg)"><svg viewBox="0 0 12 12" width="12" height="12"><path d="M6 1 L10 6 L2 6 Z" fill="#000" stroke="#000" stroke-width="0.5"/></svg></span>`
          currentMarker = new maplibregl.Marker({ element: el })
            .setLngLat([currentPosition.lng, currentPosition.lat])
            .addTo(mapInstance)
            
        }





        // Click handler for forecast points
        if (geojson) {
          const pointLayerIds = ["geojson-points", "geojson-current-point", "geojson-historic-points", "geojson-point-labels"]
          for (const layerId of pointLayerIds) {
            if (mapInstance.getLayer(layerId)) {
              mapInstance.on("mouseenter", layerId, () => {
                mapInstance.getCanvas().style.cursor = "pointer"
              })
              mapInstance.on("mouseleave", layerId, () => {
                mapInstance.getCanvas().style.cursor = ""
              })
              mapInstance.on("click", layerId, (e) => {
                const feat = e.features?.[0]
                if (!feat || feat.geometry?.type !== "Point") return
                const [lng, lat] = feat.geometry.coordinates
                const dateStr = feat.properties?.date
                let dateLine = "—"
                if (dateStr) {
                  const tzInfo = getTimezoneInfo(lat, lng, dateStr)
                  if (tzInfo.formattedLocal && tzInfo.timeZoneLabel) {
                    dateLine = `${tzInfo.formattedLocal}, ${tzInfo.timeZoneLabel}.`
                  } else if (tzInfo.formattedLocal) {
                    dateLine = `${tzInfo.formattedLocal}.`
                  } else {
                    dateLine = `${new Date(dateStr).toLocaleDateString(undefined, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}.`
                  }
                }
                const latFormatted =
                  lat >= 0 ? `${lat.toFixed(1)}°N` : `${Math.abs(lat).toFixed(1)}°S`
                let relativeTime = ""
                if (dateStr) {
                  const diffMs = new Date(dateStr).getTime() - Date.now()
                  const diffMins = Math.abs(Math.floor(diffMs / 60000))
                  const diffHours = Math.floor(diffMins / 60)
                  const mins = diffMins % 60
                  const diffDays = Math.floor(diffHours / 24)
                  if (diffMs >= 0) {
                    if (diffMins < 1) relativeTime = "estimated now"
                    else if (diffMins < 60) relativeTime = `estimated in ${diffMins} minute${diffMins === 1 ? "" : "s"}`
                    else if (diffHours < 24) relativeTime = mins > 0
                      ? `estimated in ${diffHours} hour${diffHours === 1 ? "" : "s"} ${mins} minute${mins === 1 ? "" : "s"}`
                      : `estimated in ${diffHours} hour${diffHours === 1 ? "" : "s"}`
                    else relativeTime = `estimated in ${diffDays} day${diffDays === 1 ? "" : "s"}`
                  } else {
                    if (diffMins < 1) relativeTime = "just now"
                    else if (diffMins < 60) relativeTime = `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`
                    else if (diffHours < 24) relativeTime = mins > 0
                      ? `${diffHours} hour${diffHours === 1 ? "" : "s"} ${mins} minute${mins === 1 ? "" : "s"} ago`
                      : `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`
                    else relativeTime = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
                  }
                }
                if (forecastPopup) forecastPopup.remove()
                forecastPopup = new Popup()
                  .setLngLat([lng, lat])
                  .setHTML(
                    `<div class="forecast-point-popup"><strong>${dateLine}</strong>${relativeTime ? `<br><span class="forecast-point-relative">${relativeTime}</span>` : ""}</div>`,
                  )
                  .addTo(mapInstance)
              })
            }
          }
        }
        applyViewFromGeoJson()
        updateMinimap() // Initial update
        // Listen to map movement and zoom events
        mapInstance.on("move", () => {
          updateMinimap()
          logMapState()
        })
        mapInstance.on("moveend", updateMinimap)
        mapInstance.on("zoom", () => {
          updateMinimap()
          logMapState()
        })
        mapInstance.on("zoomend", updateMinimap)
        mapInstance.on("resize", updateMinimap)
      } finally {
        isLoading = false
        mapLoaded = true
      }
    }

    // Granular load timing — identify what's slow
    mapInstance.once("styledata", () => {
      console.log("[carto] styledata — style parsed/applied, clearing spinner")
      addDiagonalPatternImage()
      addCategoryLabelImages()
      isLoading = false
      mapLoaded = true
    })
    mapInstance.on("sourcedataloading", (e) => {
      if (e.sourceId) console.log(`[carto] sourcedataloading: ${e.sourceId}${e.tile ? ` z${e.tile.tileID?.overscaledZ}` : ""}`)
    })
    mapInstance.on("sourcedata", (e) => {
      if (e.isSourceLoaded) console.log(`[carto] source fully loaded: ${e.sourceId}`)
    })
    mapInstance.once("idle", () => {
      console.log("[carto] idle — all tiles rendered")
    })

    // Clear loading state if map errors (e.g. tile/glyph network failure)
    mapInstance.on("error", (e) => {
      console.error("[carto] MapLibre error:", e.error)
      isLoading = false
    })

    // Check if map is already loaded (can happen in article format)
    if (mapInstance.loaded()) {
      setupMapAfterLoad()
    } else {
      mapInstance.once("load", setupMapAfterLoad)
    }

    mapInstance.on("load", () => {
      mapInstance.resize()
    })

    // mapInstance.on('resize', () => {
    //   console.log('resize');
    //   mapInstance.setCenter(center);
    //   // mapInstance.resize();
    // });
  })
</script>

<div class="atom" bind:clientWidth={width}>
  <div id="graphicContainer">

    <div class="vis-chart-headline">{headline}</div>
    
    <div class="vis-chart-standfirst">{@html subtitle}</div>

    {#if showTimeLegend}
    <div class="map-legends">
      <div class="category-legend" role="img" aria-label="Cyclone category scale: 1 lightest to 5 darkest; tropical low and historic points">
        <div class="category-legend-row">
          <div class="category-legend-col">
            <span class="category-legend-label">Cyclone category</span>
            <div class="category-legend-swatches">
              {#each [
                { cat: 1, color: '#ffffb2', dark: false },
                { cat: 2, color: '#fecc5c', dark: false },
                { cat: 3, color: '#fd8d3c', dark: false },
                { cat: 4, color: '#f03b20', dark: true },
                { cat: 5, color: '#bd0026', dark: true },
              ] as item (item.cat)}
                <div class="category-legend-swatch" class:dark={item.dark} style="background-color: {item.color}">
                  <span class="category-legend-value">{item.cat}</span>
                </div>
              {/each}
            </div>
          </div>
          <div class="category-legend-col">
            <span class="category-legend-label">Other points</span>
            <div class="track-points-legend-items">
              <div class="track-points-legend-item">
                <span class="category-legend-swatch track-points-legend-swatch" style="background-color: #888"></span>
                <span class="warning-legend-text">Tropical low</span>
              </div>
              <div class="track-points-legend-item">
                <span class="category-legend-swatch track-points-legend-swatch" style="background-color: #fff"></span>
                <span class="warning-legend-text">Historic</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="warning-legend" role="img" aria-label="Warning areas: Watch and Warn">
        <span class="warning-legend-label">Wind alert</span>
        <div class="warning-legend-items">
          <div class="warning-legend-item">
            <span class="warning-legend-swatch" style="background-color: rgba(153, 142, 195, 0.5)"></span>
            <span class="warning-legend-text">Warning</span>
          </div>
          <div class="warning-legend-item">
            <span class="warning-legend-swatch" style="background-color: rgba(84, 39, 136, 0.5)"></span>
            <span class="warning-legend-text">Watch</span>
          </div>
          <div class="warning-legend-item">
            <span class="warning-legend-swatch warning-legend-diagonal"></span>
            <span class="warning-legend-text">Possible cyclone range</span>
          </div>
        </div>
      </div>
    </div>
    {/if}

    <div
      id="cartoMap"
      class={MAP_INTERACTIVE ? "interactive" : "non-interactive"}
    >
      {#if isLoading}
        <div class="loading-overlay" role="status" aria-live="polite">
          <div class="spinner" aria-hidden="true"></div>
          <span class="loading-text">Loading map…</span>
        </div>
      {/if}
      <div id="minimap"></div>
    </div>

    <div class="vis-chart-source">{@html source}</div>	
  </div>
</div>

<style lang="scss">
  :global(
      .non-interactive .maplibregl-canvas-container.maplibregl-interactive
    ) {
    cursor: default !important;
  }

  .atom {
    width: 100%;
    position: relative;

    #cartoMap {
      width: 100%;
      height: 500px;
      position: relative;

      .loading-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        background: rgba(255, 255, 255, 0.8);
        z-index: 2000;

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #ccc;
          border-top-color: #333;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-text {
          font-size: 14px;
          color: #333;
        }
      }
    }

    :global(.maplibregl-ctrl-scale) {
      background: none;
      border-bottom: 2px solid #333;
      border-left: none;
      border-right: none;
      border-top: #333;
      box-sizing: border-box;
      color: #333;
      font-size: 10px;
      padding: 0 5px;
      white-space: nowrap;
    }

    .map-legends {
      display: flex;
      flex-wrap: wrap;
      gap: 24px 32px;
      margin-bottom: 12px;
      font-family: "Guardian Text Sans Web", sans-serif;
    }

    .category-legend {
      font-size: 12px;
      color: #333;

      .category-legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 24px 32px;
      }

      .category-legend-col {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .category-legend-col > .category-legend-label {
        margin-bottom: 0;
      }

      .category-legend-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        font-family: "Guardian Text Sans Web", sans-serif;
      }

      .category-legend-swatches {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .category-legend-swatch {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1px solid #000;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .category-legend-value {
        font-size: 11px;
        font-weight: 600;
        color: #333;
        text-shadow: 0 0 1px #fff;
        font-family: "Guardian Text Sans Web", sans-serif;
      }

      .category-legend-swatch.dark .category-legend-value {
        color: #fff;
        text-shadow: 0 0 1px rgba(0, 0, 0, 0.5);
      }

      .track-points-legend-items {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 16px;
        align-items: center;
      }

      .track-points-legend-item {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .track-points-legend-swatch {
        flex-shrink: 0;
      }
    }

    .warning-legend {
      font-size: 12px;
      color: #333;

      .warning-legend-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        font-family: "Guardian Text Sans Web", sans-serif;
      }

      .warning-legend-items {
        display: flex;
        gap: 16px;
        align-items: center;
      }

      .warning-legend-item {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .warning-legend-swatch {
        width: 16px;
        height: 16px;
        border: 1px solid rgba(0, 0, 0, 0.2);
        display: inline-block;
      }

      .warning-legend-swatch.warning-legend-diagonal {
        background: repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 2px,
          #888 2px,
          #888 3px
        );
      }

      .warning-legend-text {
        font-family: "Guardian Text Sans Web", sans-serif;
      }
    }

    #minimap {
      position: absolute;
      bottom: 0px;
      left: -15px;
      z-index: 1000;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  :global(.forecast-point-popup) {
    padding: 4px 0;
    font-size: 13px;
    line-height: 1.4;
  }

  :global(.forecast-point-relative) {
    font-size: 12px;
    color: #666;
    font-weight: normal;
  }

  /* Pulse marker for current cyclone position */
  #cartoMap :global(.cyclone-pulse-marker) {
    position: relative;
    width: 24px;
    height: 24px;
    pointer-events: none;
  }

  #cartoMap :global(.cyclone-pulse-marker .pulse-arrow) {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transform-origin: center center;
    z-index: 1;
  }

  #cartoMap :global(.cyclone-pulse-marker .pulse-arrow svg) {
    filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.3));
  }

  #cartoMap :global(.cyclone-pulse-marker .pulse-ring) {
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #c70000;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: cyclone-pulse 2s ease-out infinite;
  }

  @keyframes cyclone-pulse {
    0% {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0.9;
    }
    100% {
      transform: translate(-50%, -50%) scale(2.5);
      opacity: 0;
    }
  }
</style>
